import "shared/extensions/date";

import { Controller, FieldErrors, useForm } from "react-hook-form";

import { DivisionSelect } from "UIPalette/Components/DivisionSelect/DivisionSelect";
import ErrorAlert from "shared/UI/Alerts/ErrorAlert";
import { GenderSelect } from "UIPalette/Components/GenderSelect/GenderSelect";
import { NoValueSelected } from "shared/constants/SelectConstants";
import Button from "shared/UI/Buttons/Button";
import { Input } from "shared/UI/Input/Input";
import { PhoneNumberInput, PhoneNumberInputValue } from "shared/UI/PhoneNumberInput/PhoneNumberInput";
import { ErrorShake, vibrateOnError } from "shared/UI/ErrorFeedback/ErrorFeedback";
import { ReactComponent as ErrorIcon } from "@icons/error.svg";
import { playErrorHaptic } from "core/javascriptChannels";
import { isSupportedCountry } from "react-phone-number-input/input";
import { UpdateUserCommand, UserResponseModel } from "shared/request/myHealthyAdvantageApi";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import InputDatePicker from "shared/UI/DatePicker/InputDatePicker/InputDatePicker";

export type Inputs = {
  forenames: string;
  surname: string;
  dateOfBirth: Date;
  gender: string;
  phone: PhoneNumberInputValue | undefined;
  divisionId: string | undefined;
};

export const getSavedPhoneCountry = (user: UserResponseModel | undefined) => {
  const countryCode = user?.countryCode;
  return countryCode && isSupportedCountry(countryCode) ? countryCode : undefined;
};

export const toPhoneFields = (phone: PhoneNumberInputValue | undefined): Pick<UpdateUserCommand, "phoneNumber" | "countryCode" | "diallingCode"> =>
  phone?.number ? { phoneNumber: phone.number, countryCode: phone.country, diallingCode: phone.dialCode } : { phoneNumber: "" };

const MIN_AGE = 16;

const FIELD_ORDER: (keyof Inputs)[] = ["forenames", "surname", "dateOfBirth", "gender", "phone", "divisionId"];

type PersonalInfoFormProps = {
  user: any;
  onSubmit: any;
  errorMessage: any;
  type: "additionalInfo" | "personalDetails";
};

export default function PersonalInfoForm({ user, onSubmit, errorMessage, type }: PersonalInfoFormProps) {
  const { t } = useTranslation();
  const { t: tPhone } = useTranslation(undefined, { keyPrefix: "phoneNumberInput" });
  const phoneErrorId = useId();
  const [failedSubmit, setFailedSubmit] = useState<{ count: number; invalidFields: (keyof Inputs)[] }>({ count: 0, invalidFields: [] });
  const [failedPhoneSubmitCount, setFailedPhoneSubmitCount] = useState(0);
  const hasPhoneDefault = useRef(false);
  const isMobileApp = !!window.flutter_inappwebview;

  const phoneDefaultCountry = getSavedPhoneCountry(user);

  const {
    handleSubmit,
    register,
    reset,
    resetField,
    getValues,
    control,
    clearErrors,
    formState: { errors, defaultValues, isSubmitting, isDirty, isSubmitSuccessful },
  } = useForm<Inputs>({
    defaultValues:
      type === "additionalInfo"
        ? {
            forenames: user?.forenames ?? "",
            surname: user?.surname ?? "",
            dateOfBirth: Date.fromDateOnly(user?.dateOfBirth),
            gender: user?.gender ?? "",
            phone: undefined,
            divisionId: user?.division?.id ?? NoValueSelected,
          }
        : {
            forenames: user?.forenames ?? "",
            surname: user?.surname ?? "",
            dateOfBirth: user?.dateOfBirth ? Date.fromDateOnly(user.dateOfBirth) : undefined,
            gender: user?.gender ?? "",
            phone: undefined,
            divisionId: user?.division?.id ?? NoValueSelected,
          },
  });

  const isMinimumAge = (inputDate: Date) => {
    const today = new Date();
    const birth = new Date(inputDate);
    const targetDate = new Date(birth.setFullYear(birth.getFullYear() + MIN_AGE));

    return today > targetDate || t(`${type}.dateOfBirthMinAgeErrorMessage`, { minAge: MIN_AGE });
  };

  const dobValidationRules = {
    required: t(`${type}.dateOfBirthRequiredErrorMessage`),
    validate: {
      isMinimumAge,
    },
  };

  useEffect(() => {
    reset(getValues());
  }, [reset, getValues, isSubmitSuccessful]);

  const onInvalid = (fieldErrors: FieldErrors<Inputs>) => {
    const invalidFields = FIELD_ORDER.filter((field) => fieldErrors[field]);
    setFailedSubmit(({ count }) => ({ count: count + 1, invalidFields }));

    if (fieldErrors.phone) {
      setFailedPhoneSubmitCount((count) => count + 1);
    } else {
      (isMobileApp ? playErrorHaptic : vibrateOnError)();
    }
  };

  const shakeProps = (field: keyof Inputs) => ({ hasError: failedSubmit.invalidFields.includes(field), failedSubmitCount: failedSubmit.count });

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit, onInvalid)} id={type} className="space-y-6">
        <div className="space-y-4">
          <ErrorShake {...shakeProps("forenames")}>
            <Input
              label={t(`${type}.forenames`)}
              defaultValue={defaultValues?.forenames}
              state={errors.forenames ? "error" : undefined}
              helperText={errors.forenames?.message}
              placeholder={t(`${type}.forenamesPlaceholder`)}
              {...register("forenames", { required: t(`${type}.forenamesErrorMessage`) })}
            />
          </ErrorShake>

          <ErrorShake {...shakeProps("surname")}>
            <Input
              label={t(`${type}.surname`)}
              defaultValue={defaultValues?.surname}
              state={errors.surname ? "error" : undefined}
              helperText={errors.surname?.message}
              placeholder={t(`${type}.surnamePlaceholder`)}
              {...register("surname", { required: t(`${type}.surnameErrorMessage`) })}
            />
          </ErrorShake>

          <Controller
            name={"dateOfBirth"}
            control={control}
            rules={dobValidationRules}
            render={({ field: { onChange, value } }) => {
              return (
                <ErrorShake {...shakeProps("dateOfBirth")}>
                  <InputDatePicker
                    maxDate={new Date()}
                    label={t(`${type}.dateOfBirth`)}
                    date={value}
                    state={errors.dateOfBirth ? "error" : undefined}
                    helperText={errors.dateOfBirth?.message}
                    setDate={onChange}
                    placeholder={t(`${type}.dateOfBirthPlaceholder`)}
                  />
                </ErrorShake>
              );
            }}
          />

          <Controller
            name={"gender"}
            control={control}
            rules={{
              required: t(`${type}.genderErrorMessage`),
            }}
            render={({ field: { onChange, value } }) => (
              <ErrorShake {...shakeProps("gender")}>
                <GenderSelect
                  helperText={errors.gender?.message}
                  state={errors.gender ? "error" : undefined}
                  onChange={onChange}
                  label={t(`${type}.gender`)}
                  value={value}
                  placeholder={t(`${type}.genderPlaceholder`)}
                />
              </ErrorShake>
            )}
          />

          <Controller
            name={"phone"}
            control={control}
            rules={{
              validate: (phone) => !phone?.number || phone.isValid || t(`${type}.phoneNumberErrorMessage`),
            }}
            render={({ field: { onChange } }) => (
              <div className="space-y-1">
                <span aria-hidden="true" className="font-bold text-navy-blue text-base">
                  {t(`${type}.phoneNumber`)}
                </span>
                <PhoneNumberInput
                  countrySelectorLabel={tPhone("countrySelectorLabel")}
                  numberInputLabel={t(`${type}.phoneNumber`)}
                  placeholder={tPhone("placeholder")}
                  defaultValue={user?.phoneNumber ?? undefined}
                  defaultCountry={phoneDefaultCountry}
                  hasError={!!errors.phone}
                  errorMessageId={phoneErrorId}
                  failedSubmitCount={failedPhoneSubmitCount}
                  focusOnFailedSubmit={failedSubmit.invalidFields[0] === "phone"}
                  onErrorReset={() => clearErrors("phone")}
                  onErrorHaptic={isMobileApp ? playErrorHaptic : undefined}
                  onChange={(phone) => {
                    if (!hasPhoneDefault.current) {
                      hasPhoneDefault.current = true;
                      resetField("phone", { defaultValue: phone });
                      return;
                    }
                    onChange(phone);
                  }}
                />
                {errors.phone && (
                  <div className="flex items-center mt-2">
                    <ErrorIcon aria-hidden="true" width={16} height={18} />
                    <p id={phoneErrorId} className="pl-1 text-sm">
                      {errors.phone.message}
                    </p>
                  </div>
                )}
              </div>
            )}
          />

          <Controller
            name={"divisionId"}
            control={control}
            render={({ field: { onChange, value } }) => <DivisionSelect onChange={onChange} label={t(`${type}.division`)} value={value} />}
          />
        </div>

        <Button disabled={!isDirty || isSubmitting} type="submit" text={t(`${type}.submitForm`)} aria-label={t(`${type}.submitForm`)} />
      </form>

      {errorMessage && (
        <div className="mt-2">
          <ErrorAlert content={t(`${type}.requestErrorMessage`)} />
        </div>
      )}
    </>
  );
}
